import React from 'react';
import * as styles from './AdminWebhookForm.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import Form, { Field, Submit } from '../../components/Form';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { TopicPrivacy, WebhookEvent } from '../../graphql/generated/graphql';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import type {
  EntitySearchTopicResult,
  EntitySelectConfig,
} from '../../search/entitySearch/EntitySelect';
import EntitySelect from '../../search/entitySearch/EntitySelect';
import SettingsContainerView from '../../settings/SettingsContainerView';
import SettingsSubmitConfirmation, {
  useSubmitWithSaved,
} from '../../settings/SettingsSubmitConfirmation';
import { UIActions } from '../../ui/uiSlice';
import nonEmpty from '../../utils/nonEmpty';

const ADMIN_WEBHOOK_FORM_TOPIC_FRAGMENT = gql(`
  fragment AdminWebhookFormTopic on Topic {
    id
  }
`);

const ADMIN_WEBHOOK_FORM_WEBHOOK_FRAGMENT = gql(`
  fragment AdminWebhookForm_webhook on Webhook {
    id
    name
    targetUrl
    enableAllPosts
    events
    secret
    topics {
      id
      ...AdminWebhookFormTopic
    }
  }
`);

type WebhookInternalValues = {
  name: string;
  targetUrl: string;
  enableAllPosts: 'ENABLED' | 'DISABLED';
  events: WebhookEvent[];
  topics: DocumentType<typeof ADMIN_WEBHOOK_FORM_TOPIC_FRAGMENT>[];
};

export type WebhookValues = Omit<
  WebhookInternalValues,
  'enableAllPosts' | 'topics'
> & {
  enableAllPosts: boolean;
  topics: string[];
};

interface AdminWebhookFormProps {
  onSubmit: (values: WebhookValues) => Promise<unknown>;
  webhook?: DocumentType<typeof ADMIN_WEBHOOK_FORM_WEBHOOK_FRAGMENT> | null;
  onRegenerateSecret?: () => Promise<void>;
  onDelete?: () => void;
}

const SEARCH_CONFIG: EntitySelectConfig = {
  topic: {
    filter: (t) => t?.privacy !== TopicPrivacy.Secret,
  },
};

const AdminWebhookForm = ({
  onSubmit,
  webhook,
  onRegenerateSecret,
  onDelete,
}: AdminWebhookFormProps) => {
  const dispatch = useAppDispatch();

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const { saved, handleSubmit } = useSubmitWithSaved(
    (values: WebhookInternalValues) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const enableAllPosts = values.enableAllPosts === 'ENABLED';
      return onSubmit({
        ...values,
        enableAllPosts,
        topics: enableAllPosts
          ? []
          : values.topics.map((topic) => topic.id).filter(nonEmpty),
      });
    },
  );

  const handleCopyButtonClick = async () => {
    if (webhook?.secret) {
      await navigator.clipboard.writeText(webhook.secret);
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Secret copied',
        }),
      );
    }
  };

  const handleRegenerateSecret = () => {
    if (!onRegenerateSecret) return;

    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Regenerate API token?',
        message: 'The current API token will stop working.',
        confirmButtonText: 'Regenerate',
        onConfirm: onRegenerateSecret,
      }),
    );
  };

  return (
    <SettingsContainerView
      parentTitle="Developer"
      parentLink="/admin/developer"
      title={<h2>{webhook?.name ?? 'New webhook'}</h2>}
    >
      <Form<WebhookInternalValues>
        initialValues={{
          name: webhook?.name ?? '',
          targetUrl: webhook?.targetUrl ?? '',
          enableAllPosts:
            !webhook || webhook.enableAllPosts ? 'ENABLED' : 'DISABLED',
          events: webhook?.events?.filter(nonEmpty) ?? [],
          topics: webhook?.topics?.filter(nonEmpty) ?? [],
        }}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <>
            <div className={styles.section}>
              <Field
                label="Webhook name"
                name="name"
                placeholder="Event notifier"
                autoFocus
                required
              />
              <Field
                label="Payload URL"
                name="targetUrl"
                placeholder="https://api.target.dev/hooks/"
                required
              />
            </div>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Scope</div>
              <div className={styles.sectionText}>
                Webhook will observe changes for these posts
              </div>
              <Field
                label="All posts (excludes posts in Private and Secret topics)"
                name="enableAllPosts"
                type="radio"
                value="ENABLED"
              />
              <Field
                label="Posts in selected topics and their subtopics"
                name="enableAllPosts"
                type="radio"
                value="DISABLED"
              />
              {values.enableAllPosts === 'DISABLED' && (
                <EntitySelect
                  isClearable={false}
                  onChange={(value) => {
                    setFieldValue(
                      'topics',
                      value.map(
                        (topic) => (topic as EntitySearchTopicResult).topic,
                      ),
                    );
                  }}
                  config={SEARCH_CONFIG}
                  value={values.topics.map(
                    (topic) => ({ type: 'topic', id: topic.id }) as const,
                  )}
                  isMulti
                />
              )}
            </div>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Events to send</div>
              <div className={styles.sectionText}>
                Anytime an event occurs, it will be sent to the payload URL
              </div>
              <div className={styles.eventsSection}>
                <Field
                  label="Post publish"
                  name="events"
                  type="checkbox"
                  value={WebhookEvent.PostPublish}
                />
                <Field
                  label="Post edit"
                  name="events"
                  type="checkbox"
                  value={WebhookEvent.PostEdit}
                />
                <Field
                  label="Post update"
                  name="events"
                  type="checkbox"
                  value={WebhookEvent.PostUpdate}
                />
                <Field
                  label="Post delete"
                  name="events"
                  type="checkbox"
                  value={WebhookEvent.PostDelete}
                />
              </div>
            </div>
            {webhook?.secret && (
              <div className={styles.section}>
                <Field
                  label="Secret"
                  name="secret"
                  value={webhook.secret}
                  readOnly
                  helpText={
                    <div className={styles.secretHelpText}>
                      <div>Token is used as a key for outgoing webhook</div>
                      <div>
                        <Button variant="link" onClick={handleRegenerateSecret}>
                          Regenerate
                        </Button>
                        {' · '}
                        <Button variant="link" onClick={handleCopyButtonClick}>
                          Copy
                        </Button>
                      </div>
                    </div>
                  }
                />
              </div>
            )}
            <div className={styles.footer}>
              <div>
                {saved && <SettingsSubmitConfirmation />}
                <Submit size="large">{webhook ? 'Save' : 'Create'}</Submit>
              </div>
              {webhook && onDelete && (
                <Button
                  icon={<SVGIcon>{trashIcon}</SVGIcon>}
                  danger
                  onClick={onDelete}
                >
                  Delete webhook
                </Button>
              )}
            </div>
          </>
        )}
      </Form>
    </SettingsContainerView>
  );
};

export default AdminWebhookForm;
